import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { CreateOrderActions, OnApproveActions, OnApproveData, PayPalScriptOptions } from '@paypal/paypal-js';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMenu } from '../contexts/MenuContext';
import { useTenant } from '../contexts/TenantContext';
import { usePublicAuth } from '../contexts/PublicAuthContext';
import {
    createDeliveryOrder,
    DeliveryOrderItemPayload,
    DeliveryOrderRequestPayload,
    DeliveryPaymentMethod,
    DeliveryPaymentMethodType,
    DeliverySettings,
    getDeliverySettings,
    getPayPalPublicConfig,
    DeliveryOrderResponse,
    PayPalPublicConfig,
} from '../services/deliveryService';
import './Delivery.css';

const Delivery: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const tenantCtx = useTenant();
    const menu = useMenu();
    const auth = usePublicAuth();

    if (!tenantCtx) {
        return <div className="delivery-page delivery-page__empty">{t('order.tenantUnavailable')}</div>;
    }

    if (!menu) {
        return <div className="delivery-page delivery-page__empty">{t('order.genericError')}</div>;
    }

    const { tenantId, tenantSlug, features } = tenantCtx;
    const { cart, clearCart } = menu;

    const tenantDeliveryKey = tenantSlug ?? tenantId ?? null;

    const [settings, setSettings] = useState<DeliverySettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [notes, setNotes] = useState('');

    const [fullName, setFullName] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [doorbell, setDoorbell] = useState('');
    const [additionalInfo, setAdditionalInfo] = useState('');

    const [selectedPayment, setSelectedPayment] = useState<DeliveryPaymentMethodType | null>(null);

    const [paypalConfigs, setPaypalConfigs] = useState<Record<string, PayPalPublicConfig>>({});
    const paypalConfig = tenantDeliveryKey ? paypalConfigs[tenantDeliveryKey] ?? null : null;
    const [paypalLoading, setPaypalLoading] = useState(false);
    const [paypalError, setPaypalError] = useState<string | null>(null);
    const [paypalOrderId, setPaypalOrderId] = useState<string | null>(null);
    const [paypalCaptureTotal, setPaypalCaptureTotal] = useState<number | null>(null);
    const [paypalPayerEmail, setPaypalPayerEmail] = useState<string | null>(null);
    const [completedOrder, setCompletedOrder] = useState<DeliveryOrderResponse | null>(null);

    useEffect(() => {
        if (!tenantDeliveryKey) {
            setLoading(false);
            setError(t('delivery.settingsError'));
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getDeliverySettings(tenantDeliveryKey, tenantId)
            .then((response) => {
                if (cancelled) return;
                setSettings(response);
            })
            .catch((err) => {
                if (cancelled) return;
                const message = err instanceof Error ? err.message : t('delivery.settingsError');
                setError(message);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [tenantDeliveryKey, tenantId, t]);

    useEffect(() => {
        if (!successMessage) return;
        const timer = window.setTimeout(() => setSuccessMessage(null), 6000);
        return () => window.clearTimeout(timer);
    }, [successMessage]);

    useEffect(() => {
        if (!validationError) return;
        const timer = window.setTimeout(() => setValidationError(null), 6000);
        return () => window.clearTimeout(timer);
    }, [validationError]);

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.user) return;
        const user = auth.user;
        setCustomerEmail((prev) => prev || user.email);
        setCustomerName((prev) => prev || user.fullName || user.email);
        setFullName((prev) => prev || user.fullName || user.email);
    }, [auth.isAuthenticated, auth.user]);

    useEffect(() => {
        if (completedOrder) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [completedOrder]);

    useEffect(() => {
        if (selectedPayment !== 'PAYPAL') {
            setPaypalOrderId(null);
            setPaypalCaptureTotal(null);
            setPaypalPayerEmail(null);
            setPaypalError(null);
            return;
        }
        if (!tenantDeliveryKey) {
            setPaypalError(t('delivery.payment.paypalConfigError', { defaultValue: 'Tenant non valido per PayPal.' }));
            return;
        }
        if (paypalConfig) {
            if (!paypalConfig.clientId) {
                setPaypalError((prev) => prev ?? t('delivery.payment.paypalConfigError', { defaultValue: 'Configurazione PayPal non valida.' }));
            }
            return;
        }
        let cancelled = false;
        setPaypalLoading(true);
        setPaypalError(null);
        getPayPalPublicConfig(tenantDeliveryKey, tenantId)
            .then((config) => {
                if (cancelled) return;
                if (!config.clientId) {
                    setPaypalError(t('delivery.payment.paypalConfigError', { defaultValue: 'Configurazione PayPal non valida.' }));
                    return;
                }
                setPaypalConfigs((prev) => ({ ...prev, [tenantDeliveryKey]: config }));
            })
            .catch((err) => {
                if (cancelled) return;
                const message = err instanceof Error
                    ? err.message
                    : t('delivery.payment.paypalConfigError', { defaultValue: 'Impossibile caricare la configurazione PayPal.' });
                setPaypalError(message);
            })
            .finally(() => {
                if (cancelled) return;
                setPaypalLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedPayment, tenantDeliveryKey, tenantId, paypalConfig, t]);

    const paymentsEnabled = useMemo(() => {
        if (!settings) return false;
        const featureSwitch = features?.deliveryPaymentEnabled ?? false;
        return Boolean(settings.deliveryPaymentFeatureEnabled && featureSwitch);
    }, [settings, features?.deliveryPaymentEnabled]);

    const guestCheckoutAllowed = useMemo(() => {
        if (!settings) return true;
        const featureSwitch = features?.deliveryGuestEnabled ?? true;
        return Boolean(settings.allowGuestCheckout && featureSwitch);
    }, [settings, features?.deliveryGuestEnabled]);

    const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);

    const subtotal = useMemo(() => cart.reduce((sum, item) => sum + item.price * item.quantity, 0), [cart]);

    const deliveryFee = settings?.flatFee ?? 0;
    const total = subtotal + deliveryFee;

    useEffect(() => {
        if (selectedPayment !== 'PAYPAL') return;
        if (paypalCaptureTotal === null) return;
        if (Math.abs(total - paypalCaptureTotal) > 0.009) {
            setPaypalOrderId(null);
            setPaypalCaptureTotal(null);
            setPaypalPayerEmail(null);
            setPaypalError(t('delivery.payment.paypalAmountChanged', { defaultValue: 'Totale aggiornato, ripeti la conferma su PayPal.' }));
        }
    }, [selectedPayment, total, paypalCaptureTotal, t]);

    const formatCurrency = useCallback((amount: number) => {
        const currency = settings?.currency || 'EUR';
        try {
            return new Intl.NumberFormat(i18n.language, { style: 'currency', currency }).format(amount);
        } catch (_) {
            return `${amount.toFixed(2)} ${currency}`;
        }
    }, [i18n.language, settings?.currency]);

    const availablePaymentMethods = useMemo(() => {
        if (!paymentsEnabled || !settings) return [] as DeliveryPaymentMethod[];
        const eligibleTotal = total;
        return settings.paymentMethods
            .filter((method) => method.active)
            .filter((method) => {
                if (!method.maxOrderTotal || method.maxOrderTotal <= 0) return true;
                return eligibleTotal <= method.maxOrderTotal;
            });
    }, [paymentsEnabled, settings, total]);

    useEffect(() => {
        if (selectedPayment && !paymentsEnabled) {
            setSelectedPayment(null);
            return;
        }
        if (selectedPayment && !availablePaymentMethods.some((method) => method.type === selectedPayment)) {
            setSelectedPayment(null);
        }
    }, [paymentsEnabled, availablePaymentMethods, selectedPayment]);

    const serviceAvailable = Boolean(settings?.enabled && settings.deliveryFeatureEnabled);

    const accountRequired = serviceAvailable && !guestCheckoutAllowed;
    const accountReady = auth.isAuthenticated;

    const paypalOptions = useMemo<PayPalScriptOptions | null>(() => {
        if (!paypalConfig || !paypalConfig.clientId) {
            return null;
        }
        return {
            clientId: paypalConfig.clientId,
            currency: paypalConfig.currency || 'EUR',
            intent: 'capture',
            components: 'buttons',
        };
    }, [paypalConfig]);

    const paypalTotalValue = useMemo(() => total.toFixed(2), [total]);

    const handleSuccessGoMenu = useCallback(() => {
        setCompletedOrder(null);
        navigate('/menu');
    }, [navigate]);

    const handleSuccessNewOrder = useCallback(() => {
        setCompletedOrder(null);
        setSuccessMessage(null);
        setValidationError(null);
    }, []);

    const minOrderReached = useMemo(() => {
        if (!settings?.minOrderAmount) return true;
        return subtotal >= settings.minOrderAmount;
    }, [settings?.minOrderAmount, subtotal]);

    const maxOrderExceeded = useMemo(() => {
        if (!settings?.maxOrderAmount || settings.maxOrderAmount <= 0) return false;
        return total > settings.maxOrderAmount;
    }, [settings?.maxOrderAmount, total]);

    const hasRequiredFields = Boolean(
        fullName.trim() &&
        street.trim() &&
        city.trim() &&
        postalCode.trim() &&
        customerPhone.trim()
    );

    const resolvePaymentLabel = useCallback((method: DeliveryPaymentMethod) => {
        if (method.displayName && method.displayName.trim()) return method.displayName;
        return t(`delivery.payment.methodNames.${method.type}`, { defaultValue: method.type });
    }, [t]);

    const mapCartItemsToPayload = useCallback((): DeliveryOrderItemPayload[] => {
        return cart.map((item) => ({
            productId: item.productId,
            productName: item.name,
            variantName: item.variantName || undefined,
            quantity: item.quantity,
            unitPrice: Number(item.price.toFixed(2)),
            totalPrice: Number((item.price * item.quantity).toFixed(2)),
            notes: item.comment?.trim() || undefined,
        }));
    }, [cart]);

    if (completedOrder) {
        const isPayPal = completedOrder.paymentMethod === 'PAYPAL';
        const totalLabel = formatCurrency(completedOrder.total ?? total);

        return (
            <div className="delivery-page delivery-page__complete">
                <div className="delivery-complete" role="status">
                    <div className="delivery-complete__icon" aria-hidden="true">✓</div>
                    <h1>{t('delivery.complete.title', { defaultValue: 'Ordine confermato!' })}</h1>
                    <p>{t('delivery.complete.description', { defaultValue: 'Grazie! Il tuo ordine è stato registrato correttamente.' })}</p>
                    <div className="delivery-complete__summary">
                        <strong>{t('delivery.complete.orderCode', { defaultValue: 'Codice ordine: {{code}}', code: completedOrder.orderCode })}</strong>
                        <span>{t('delivery.complete.total', { defaultValue: 'Totale ordine: {{amount}}', amount: totalLabel })}</span>
                        {isPayPal && (
                            <span>{t('delivery.complete.paypal', { defaultValue: 'Pagamento PayPal confermato.' })}</span>
                        )}
                    </div>
                    <div className="delivery-complete__actions">
                        <button
                            type="button"
                            className="delivery-complete__primary"
                            onClick={handleSuccessGoMenu}
                        >
                            {t('delivery.complete.backToMenu', { defaultValue: 'Torna al menu' })}
                        </button>
                        <button
                            type="button"
                            className="delivery-complete__secondary"
                            onClick={handleSuccessNewOrder}
                        >
                            {t('delivery.complete.newOrder', { defaultValue: 'Nuovo ordine' })}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const submitOrder = async (options?: { paypalOrderId?: string }) => {
        if (submitting) {
            return;
        }

        setValidationError(null);
        setSuccessMessage(null);

        if (!tenantDeliveryKey || !settings) {
            setValidationError(t('delivery.settingsError'));
            return;
        }
        if (!cart.length) {
            setValidationError(t('delivery.cartEmpty'));
            return;
        }
        if (accountRequired && !accountReady) {
            setValidationError(t('delivery.auth.loginRequired'));
            auth.openModal('LOGIN', customerEmail.trim() || undefined);
            return;
        }
        if (!serviceAvailable) {
            setValidationError(t('delivery.disabled'));
            return;
        }
        if (!guestCheckoutAllowed && !auth.isAuthenticated) {
            setValidationError(t('delivery.contact.guestCheckoutDisabled'));
            return;
        }
        if (!hasRequiredFields) {
            setValidationError(t('delivery.validation.required'));
            return;
        }
        if (!minOrderReached) {
            const amount = settings.minOrderAmount ? formatCurrency(settings.minOrderAmount) : '';
            setValidationError(t('delivery.validation.minOrder', { amount }));
            return;
        }
        if (maxOrderExceeded) {
            setValidationError(t('delivery.validation.maxOrder'));
            return;
        }

        const effectivePaypalOrderId = options?.paypalOrderId ?? paypalOrderId;
        const effectivePaypalCaptureTotal = options?.paypalOrderId ? total : paypalCaptureTotal;

        if (selectedPayment === 'PAYPAL') {
            if (!effectivePaypalOrderId) {
                setValidationError(t('delivery.payment.paypalMissing', { defaultValue: 'Completa il pagamento PayPal prima di confermare.' }));
                return;
            }
            if (effectivePaypalCaptureTotal === null || Math.abs(total - effectivePaypalCaptureTotal) > 0.009) {
                setValidationError(t('delivery.payment.paypalAmountChanged', { defaultValue: 'L\'importo è cambiato, ripeti la conferma su PayPal.' }));
                return;
            }
        }

        setSubmitting(true);
        try {
            const items = mapCartItemsToPayload();
            const sanitizedEmail = auth.isAuthenticated
                ? auth.user?.email
                : customerEmail.trim() || undefined;
            const resolvedName = customerName.trim()
                || fullName.trim()
                || auth.user?.fullName
                || auth.user?.email
                || customerPhone.trim();
            const addressName = fullName.trim() || auth.user?.fullName || resolvedName;
            const payload: DeliveryOrderRequestPayload = {
                customerName: resolvedName,
                customerEmail: sanitizedEmail,
                customerPhone: customerPhone.trim(),
                guestCheckout: guestCheckoutAllowed && !auth.isAuthenticated,
                userId: auth.user?.userId,
                notes: notes.trim() || undefined,
                paymentMethod: selectedPayment ?? undefined,
                subtotal: Number(subtotal.toFixed(2)),
                deliveryFee: Number(deliveryFee.toFixed(2)),
                total: Number(total.toFixed(2)),
                address: {
                    fullName: addressName,
                    phoneNumber: customerPhone.trim(),
                    street: street.trim(),
                    city: city.trim(),
                    postalCode: postalCode.trim(),
                    additionalInfo: additionalInfo.trim() || undefined,
                    doorbell: doorbell.trim() || undefined,
                },
                items,
            };

            if (selectedPayment === 'PAYPAL' && effectivePaypalOrderId) {
                payload.paypalOrderId = effectivePaypalOrderId;
            }

            const response = await createDeliveryOrder(tenantDeliveryKey, payload, tenantId, auth.token);
            setCompletedOrder(response);
            setSuccessMessage(t('delivery.success', { code: response.orderCode }));
            clearCart();
            setNotes('');
            setSelectedPayment(null);
            setPaypalOrderId(null);
            setPaypalCaptureTotal(null);
            setPaypalPayerEmail(null);
            setPaypalError(null);
            setCustomerName('');
            if (!auth.isAuthenticated) {
                setCustomerEmail('');
            }
            setCustomerPhone('');
            setFullName('');
            setStreet('');
            setCity('');
            setPostalCode('');
            setDoorbell('');
            setAdditionalInfo('');
        } catch (err) {
            const message = err instanceof Error ? err.message : t('delivery.settingsError');
            setValidationError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = () => {
        void submitOrder();
    };

    if (loading) {
        return <div className="delivery-page delivery-page__empty">{t('delivery.loading')}</div>;
    }

    return (
        <div className="delivery-page">
            <header className="delivery-header">
                <button
                    type="button"
                    className="delivery-back"
                    onClick={() => navigate('/menu')}
                >
                    <span className="delivery-back__icon" aria-hidden="true">←</span>
                    <span>{t('order.backToMenu')}</span>
                </button>
                <div className="delivery-header__text">
                    <h1>{t('delivery.title')}</h1>
                    <p>{t('delivery.subtitle')}</p>
                </div>
            </header>

            {successMessage && (
                <div className="delivery-toast delivery-toast--success" role="status">
                    {successMessage}
                </div>
            )}

            {(error || validationError) && (
                <div className="delivery-alert" role="alert">
                    {error || validationError}
                </div>
            )}

            {settings?.suspensionMessage && (
                <div className="delivery-alert delivery-alert--muted" role="status">
                    {settings.suspensionMessage}
                </div>
            )}

            {!serviceAvailable && (
                <div className="delivery-card delivery-card__disabled">
                    <p>{t('delivery.disabled')}</p>
                </div>
            )}

            {serviceAvailable && (
                <>
                    <section className={`delivery-card delivery-auth-card ${accountReady ? 'delivery-auth-card--ready' : ''}`}>
                        <div className="delivery-card__header">
                            <div className="delivery-auth-card__headline">
                                <h2>{t('delivery.auth.title')}</h2>
                                <p>{accountReady
                                    ? t('delivery.auth.connectedDescription')
                                    : accountRequired
                                        ? t('delivery.auth.requiredDescription')
                                        : t('delivery.auth.optionalDescription')}</p>
                                {!accountRequired && !accountReady && guestCheckoutAllowed && (
                                    <p className="delivery-auth-card__guest-hint">
                                        {t('delivery.auth.guestHint', { defaultValue: 'No account? You can finish the order as a guest.' })}
                                    </p>
                                )}
                            </div>
                            {accountReady && (
                                <span className="delivery-chip delivery-chip--success">{t('delivery.auth.connectedChip')}</span>
                            )}
                        </div>
                        {accountReady && auth.user ? (
                            <div className="delivery-auth-card__profile">
                                <div className="delivery-auth-card__identity">
                                    <strong>{auth.user.fullName || auth.user.email}</strong>
                                    <span>{auth.user.email}</span>
                                </div>
                                <button
                                    type="button"
                                    className="delivery-auth-card__ghost"
                                    onClick={() => auth.logout()}
                                >
                                    {t('delivery.auth.logout')}
                                </button>
                            </div>
                        ) : (
                            <div className="delivery-auth-card__actions">
                                <button
                                    type="button"
                                    className="delivery-auth-card__primary"
                                    onClick={() => auth.openModal('LOGIN', customerEmail.trim() || undefined)}
                                >
                                    {accountRequired ? t('delivery.auth.loginCta') : t('delivery.auth.loginOptionalCta')}
                                </button>
                                <button
                                    type="button"
                                    className="delivery-auth-card__ghost"
                                    onClick={() => auth.openModal('SIGNUP', customerEmail.trim() || undefined)}
                                >
                                    {t('delivery.auth.signupCta')}
                                </button>
                            </div>
                        )}
                    </section>

                    <section className="delivery-card">
                        <div className="delivery-card__header">
                            <h2>{t('delivery.summary.title')}</h2>
                            <span className="delivery-chip">{t('delivery.summary.itemsLabel', { count: cartCount })}</span>
                        </div>
                        {cart.length === 0 ? (
                            <p className="delivery-empty">{t('delivery.cartEmpty')}</p>
                        ) : (
                            <ul className="delivery-items">
                                {cart.map((item) => (
                                    <li key={item.productId} className="delivery-item">
                                        <div className="delivery-item__info">
                                            <strong>{item.name}</strong>
                                            {item.variantName && (
                                                <span className="delivery-item__variant">{item.variantName}</span>
                                            )}
                                            {item.comment && (
                                                <span className="delivery-item__note">{item.comment}</span>
                                            )}
                                        </div>
                                        <div className="delivery-item__qty">
                                            <span>{item.quantity}×</span>
                                            <span>{formatCurrency(item.price * item.quantity)}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="delivery-summary">
                            <div>
                                <span>{t('delivery.summary.subtotal')}</span>
                                <strong>{formatCurrency(subtotal)}</strong>
                            </div>
                            <div>
                                <span>{t('delivery.summary.deliveryFee')}</span>
                                <strong>{formatCurrency(deliveryFee)}</strong>
                            </div>
                            <div className="delivery-summary__total">
                                <span>{t('delivery.summary.total')}</span>
                                <strong>{formatCurrency(total)}</strong>
                            </div>
                        </div>
                        {!minOrderReached && settings?.minOrderAmount && (
                            <p className="delivery-hint delivery-hint--warning">
                                {t('delivery.validation.minOrder', { amount: formatCurrency(settings.minOrderAmount) })}
                            </p>
                        )}
                        {maxOrderExceeded && (
                            <p className="delivery-hint delivery-hint--warning">
                                {t('delivery.validation.maxOrder')}
                            </p>
                        )}
                        {settings?.estimatedDeliveryMinutes && (
                            <p className="delivery-hint">
                                {t('delivery.summary.eta', { minutes: settings.estimatedDeliveryMinutes })}
                            </p>
                        )}
                    </section>

                    <section className="delivery-card">
                        <h2>{t('delivery.address.title')}</h2>
                        <div className="delivery-grid">
                            <label className="delivery-field">
                                <span>{t('delivery.address.fullName')}</span>
                                <input value={fullName} onChange={(event) => setFullName(event.target.value)} required />
                            </label>
                            <label className="delivery-field">
                                <span>{t('delivery.address.phone')}</span>
                                <input
                                    value={customerPhone}
                                    onChange={(event) => setCustomerPhone(event.target.value)}
                                    required
                                />
                            </label>
                            <label className="delivery-field delivery-field__full">
                                <span>{t('delivery.address.street')}</span>
                                <input value={street} onChange={(event) => setStreet(event.target.value)} required />
                            </label>
                            <label className="delivery-field">
                                <span>{t('delivery.address.city')}</span>
                                <input value={city} onChange={(event) => setCity(event.target.value)} required />
                            </label>
                            <label className="delivery-field">
                                <span>{t('delivery.address.postalCode')}</span>
                                <input value={postalCode} onChange={(event) => setPostalCode(event.target.value)} required />
                            </label>
                            <label className="delivery-field">
                                <span>{t('delivery.address.doorbell')}</span>
                                <input value={doorbell} onChange={(event) => setDoorbell(event.target.value)} />
                            </label>
                            <label className="delivery-field delivery-field__full">
                                <span>{t('delivery.address.additionalInfo')}</span>
                                <textarea
                                    value={additionalInfo}
                                    onChange={(event) => setAdditionalInfo(event.target.value)}
                                    rows={3}
                                />
                            </label>
                        </div>
                    </section>

                    <section className="delivery-card">
                        <h2>{t('delivery.contact.title')}</h2>
                        <div className="delivery-grid">
                            <label className="delivery-field">
                                <span>{t('delivery.contact.name')}</span>
                                <input value={customerName} onChange={(event) => setCustomerName(event.target.value)} />
                            </label>
                            <label className="delivery-field">
                                <span>{t('delivery.contact.email')}</span>
                                <input value={customerEmail} onChange={(event) => setCustomerEmail(event.target.value)} />
                            </label>
                            <label className="delivery-field delivery-field__full">
                                <span>{t('delivery.contact.notes')}</span>
                                <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
                            </label>
                        </div>
                        {!guestCheckoutAllowed && (
                            <p className="delivery-hint delivery-hint--warning">
                                {t('delivery.contact.guestCheckoutDisabled')}
                            </p>
                        )}
                    </section>

                    <section className="delivery-card">
                        <h2>{t('delivery.payment.title')}</h2>
                        {paymentsEnabled ? (
                            availablePaymentMethods.length > 0 ? (
                                <div className="delivery-payments">
                                    <p className="delivery-hint">{t('delivery.payment.methodOptional')}</p>
                                    <div className="delivery-payment-options">
                                        {availablePaymentMethods.map((method) => (
                                            <button
                                                key={method.type}
                                                type="button"
                                                className={`delivery-payment ${selectedPayment === method.type ? 'delivery-payment--active' : ''}`}
                                                onClick={() => setSelectedPayment((prev) => (prev === method.type ? null : method.type))}
                                            >
                                                <span className="delivery-payment__label">{resolvePaymentLabel(method)}</span>
                                                {method.instructions && (
                                                    <span className="delivery-payment__hint">{method.instructions}</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                    {selectedPayment === 'PAYPAL' && (
                                        <div className="delivery-paypal">
                                            {paypalLoading && (
                                                <p className="delivery-hint">
                                                    {t('delivery.payment.paypalLoading', { defaultValue: 'Caricamento di PayPal in corso…' })}
                                                </p>
                                            )}
                                            {paypalError && (
                                                <p className="delivery-hint delivery-hint--warning">{paypalError}</p>
                                            )}
                                            {!paypalLoading && !paypalError && paypalOrderId && (
                                                <p className="delivery-hint delivery-hint--success">
                                                    {t('delivery.payment.paypalReady', { defaultValue: 'Pagamento PayPal confermato.' })}
                                                    {paypalPayerEmail ? ` (${paypalPayerEmail})` : ''}
                                                </p>
                                            )}
                                            {!paypalLoading && !paypalError && !paypalOrderId && (
                                                <p className="delivery-hint">
                                                    {t('delivery.payment.paypalInstruction', { defaultValue: 'Conferma con PayPal per completare l\'ordine.' })}
                                                </p>
                                            )}
                                            {paypalOptions && !paypalError && (
                                                <PayPalScriptProvider options={paypalOptions}>
                                                    <PayPalButtons
                                                        style={{ layout: 'vertical', height: 45 }}
                                                        forceReRender={[paypalTotalValue, paypalOptions.currency ?? 'EUR']}
                                                        disabled={
                                                            submitting ||
                                                            paypalLoading ||
                                                            !serviceAvailable ||
                                                            !hasRequiredFields ||
                                                            !minOrderReached ||
                                                            maxOrderExceeded
                                                        }
                                                        createOrder={(_data: Record<string, unknown>, actions: CreateOrderActions) => {
                                                            if (!actions.order) {
                                                                return Promise.reject(new Error('PayPal non è pronto'));
                                                            }
                                                            return actions.order.create({
                                                                intent: 'CAPTURE',
                                                                purchase_units: [
                                                                    {
                                                                        amount: {
                                                                            currency_code: paypalOptions.currency ?? 'EUR',
                                                                            value: paypalTotalValue,
                                                                        },
                                                                    },
                                                                ],
                                                            });
                                                        }}
                                                        onApprove={async (data: OnApproveData, actions: OnApproveActions) => {
                                                            try {
                                                                const orderApi = actions?.order;
                                                                const details = orderApi ? await orderApi.capture() : undefined;
                                                                const approvedOrderId = data.orderID ?? details?.id ?? null;
                                                                if (!approvedOrderId) {
                                                                    setPaypalError(t('delivery.payment.paypalError', { defaultValue: 'Errore durante la conferma PayPal.' }));
                                                                    setPaypalOrderId(null);
                                                                    setPaypalCaptureTotal(null);
                                                                    return;
                                                                }
                                                                setPaypalOrderId(approvedOrderId);
                                                                setPaypalCaptureTotal(total);
                                                                const payerEmail = details?.payer?.email_address ?? null;
                                                                setPaypalPayerEmail(payerEmail);
                                                                setPaypalError(null);
                                                                await submitOrder({ paypalOrderId: approvedOrderId });
                                                            } catch (paypalErr) {
                                                                console.error('PayPal approval error', paypalErr);
                                                                setPaypalError(t('delivery.payment.paypalError', { defaultValue: 'Errore durante la conferma PayPal.' }));
                                                                setPaypalOrderId(null);
                                                                setPaypalCaptureTotal(null);
                                                                setPaypalPayerEmail(null);
                                                            }
                                                        }}
                                                        onCancel={() => {
                                                            setPaypalError(t('delivery.payment.paypalCancelled', { defaultValue: 'Pagamento PayPal annullato.' }));
                                                            setPaypalOrderId(null);
                                                            setPaypalCaptureTotal(null);
                                                            setPaypalPayerEmail(null);
                                                        }}
                                                        onError={(paypalErr: unknown) => {
                                                            console.error('PayPal button error', paypalErr);
                                                            const rawMessage = paypalErr instanceof Error
                                                                ? paypalErr.message
                                                                : typeof paypalErr === 'string'
                                                                    ? paypalErr
                                                                    : '';
                                                            const friendlyMessage = rawMessage.includes('Failed to load the PayPal JS SDK script')
                                                                ? t('delivery.payment.paypalSdkError', { defaultValue: 'Impossibile caricare il modulo PayPal. Verifica le credenziali o ricarica la pagina.' })
                                                                : t('delivery.payment.paypalError', { defaultValue: 'Errore durante il pagamento PayPal.' });
                                                            setPaypalError(friendlyMessage);
                                                            setPaypalOrderId(null);
                                                            setPaypalCaptureTotal(null);
                                                            setPaypalPayerEmail(null);
                                                        }}
                                                    />
                                                </PayPalScriptProvider>
                                            )}
                                            {!paypalLoading && !paypalOptions && !paypalError && (
                                                <p className="delivery-hint delivery-hint--warning">
                                                    {t('delivery.payment.paypalConfigError', { defaultValue: 'Configurazione PayPal non disponibile.' })}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="delivery-hint">{t('delivery.payment.noMethods')}</p>
                            )
                        ) : (
                            <p className="delivery-hint">{t('delivery.payment.disabled')}</p>
                        )}
                    </section>

                    <section className="delivery-actions">
                        <button
                            type="button"
                            className="delivery-submit"
                            disabled={
                                submitting ||
                                !serviceAvailable ||
                                (accountRequired && !accountReady) ||
                                !cart.length ||
                                !hasRequiredFields ||
                                !minOrderReached ||
                                maxOrderExceeded ||
                                (selectedPayment === 'PAYPAL' && !paypalOrderId)
                            }
                            onClick={() => void handleSubmit()}
                        >
                            {submitting ? t('delivery.submitting') : t('delivery.submit')}
                        </button>
                        <button
                            type="button"
                            className="delivery-secondary"
                            onClick={() => navigate('/menu')}
                        >
                            {t('order.backToMenu')}
                        </button>
                    </section>
                </>
            )}
        </div>
    );
};

export default Delivery;
